import {
  ENVIRONMENT_AUTH_SESSION_PATH,
  ENVIRONMENT_BROWSER_SESSION_PATH,
  ENVIRONMENT_SIGN_OUT_PATH,
  EnvironmentAccessExchangeRefusal,
  EnvironmentAuthSessionState,
} from "@afar-research/contracts";
import * as Schema from "effect/Schema";

import type { BrowserPairingFailure } from "./browserPairing.logic";

const decodeSessionState = Schema.decodeUnknownSync(EnvironmentAuthSessionState);
const isRefusal = Schema.is(Schema.Struct({ reason: EnvironmentAccessExchangeRefusal }));
const encodeJson = Schema.encodeSync(Schema.UnknownFromJsonString);

/**
 * The browser's own pairing with the Environment that served it.
 *
 * Every call is same-origin and carries the session cookie, which is HttpOnly: page script never
 * holds the credential, only asks the Environment what it thinks of it.
 */
export async function readBrowserSessionState(): Promise<EnvironmentAuthSessionState> {
  const response = await fetch(ENVIRONMENT_AUTH_SESSION_PATH, {
    credentials: "include",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw new Error(`Could not read this browser's session (${response.status}).`);
  return decodeSessionState(await response.json());
}

export async function pairBrowser(
  credential: string,
): Promise<
  { readonly paired: true } | { readonly paired: false; readonly failure: BrowserPairingFailure }
> {
  let response: Response;
  try {
    response = await fetch(ENVIRONMENT_BROWSER_SESSION_PATH, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: encodeJson({ credential }),
    });
  } catch {
    return { paired: false, failure: "unreachable" };
  }
  if (response.ok) return { paired: true };
  if (response.status === 403) return { paired: false, failure: "refused" };
  const body: unknown = await response.json().catch(() => null);
  const reason = isRefusal(body) ? body.reason : null;
  return {
    paired: false,
    failure: reason === "malformed" ? "malformed" : reason === null ? "refused" : "spent",
  };
}

/** Ends this browser's session on the Environment and clears its cookie. */
export async function signOutBrowser(): Promise<void> {
  const response = await fetch(ENVIRONMENT_SIGN_OUT_PATH, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) throw new Error(`Could not sign this browser out (${response.status}).`);
}
